import React from "react";
import { ThemeModeProvider, useThemeMode } from "./context/ThemeModeContext";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import Layout from "./components/layout/Layout";
import { STORAGE_KEYS } from "./utils/StorageKeys";
import { UserProvider, useUserContext } from "./context/UserContext";
import AdminDashboard from "./components/admin/AdminDashboard";
import UserManagement from "./components/admin/UserManagement";
import ChangePassword from "./screens/ChangePassword";
import MainScreen, { User } from "./screens/MainScreen";
import { lightTheme, darkTheme } from "./theme";
import LunchPollApp from "./screens/LunchPollApp";
import CabinBookingPage from "./screens/CabinBooking";
import MobileHomeScreen from "./screens/MobileHomeScreen";
import EmployeeJournal from "./screens/EmployeeJournal";
import BulletinBoardAdmin from "./screens/BulletinBoardAdmin";
import { Send } from "@mui/icons-material";
import SendVoiceScreen from "./screens/remindMeMobileScreens/SendVoiceScreen";
import SendTextScreen from "./screens/remindMeMobileScreens/SendTextScreen";
import SendImageScreen from "./screens/remindMeMobileScreens/SendImageScreen";
import { RemindMeProvider } from "./context/RemindMeContext";
import { AlertProvider } from "./context/AlertContext";
import SettingsScreen from "./screens/remindMeMobileScreens/SettingsScreen";
import ReminMeWebMainScreen from "./screens/remindMeWeb/ReminMeWebMainScreen";
import AdminJournals from "./components/admin/AdminJournals";

const AppContent: React.FC = () => {
  const { isDarkMode } = useThemeMode();
  const { currentUser, setCurrentUser, isAdmin, isAuthenticated, handleLogout } = useUserContext();

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Router>
        <Layout
          isLoggedIn={!!currentUser}
          isAdmin={isAdmin}
          onLogout={handleLogout}
        >
          <Routes>
            <Route
              path="/"
              element={
                <MainScreen />
              }
            />
            <Route
              path="/admin"
              element={
                isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/user-management"
              element={
                isAdmin ? (
                  <UserManagement />
                ) : (
                  null
                )
              }
            />
            <Route
              path="/lunch-poll"
              element={
                isAuthenticated ? (isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <LunchPollApp />
                )) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/cabin-booking"
              element={
                isAuthenticated ? (isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <CabinBookingPage />
                )) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/employee-journal"
              element={
                isAuthenticated ? (isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <EmployeeJournal />
                )) : (
                  null
                )
              }
            />
            <Route
              path="/mobile-home"
              element={
                isAuthenticated ? (isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <MobileHomeScreen />
                )) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/reset-password"
              element={
                isAuthenticated ? (
                  <ChangePassword />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/bulletin-board-admin"
              element={
                isAdmin ? (
                  <BulletinBoardAdmin />
                ) : (
                  null
                )
              }
            />
            <Route path="/noteToSelf_voice" element={<SendVoiceScreen />} />
            <Route path="/noteToSelf_text" element={<SendTextScreen />} />
            <Route path="/noteToSelf_image" element={<SendImageScreen />} />
            <Route path="/noteToSelf" element={<ReminMeWebMainScreen />} />

            <Route
              path="/noteToSelf_settings"
              element={
                isAuthenticated ? (
                  <SettingsScreen />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/journals"
              element={
                isAdmin ? (
                  <AdminJournals />
                ) : (
                  null
                )
              }
            />

            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
  );
};

const App: React.FC = () => (
  <ThemeModeProvider>
    <UserProvider>
      <RemindMeProvider>
        <AlertProvider>
          <AppContent />
        </AlertProvider>
      </RemindMeProvider>
    </UserProvider>
  </ThemeModeProvider>
);

export default App;